import { Icon } from "./icons";
import { useTranslation } from "react-i18next";
import { RegionPicker } from "./RegionPicker";
import { Card, cx } from "./ui";
import type { TFunction } from "i18next";
import type { RegionCatalogue } from "../lib/types";

/**
 * The Region section of a create form, folded by default.
 *
 * Auto is the default and the right answer for most people, so the choice
 * does not need to be open on every create. The summary line carries the
 * state instead — "Auto · Asia", the region picked, or a dedicated tenant's
 * fixed region — so nobody has to open it to know what will happen. Same
 * disclosure the app form's Advanced section uses, for the same reason.
 */
export function RegionSection({
  catalogue,
  loading,
  value,
  onChange,
}: {
  catalogue: RegionCatalogue | undefined;
  loading: boolean;
  value: string | null;
  onChange: (region: string | null) => void;
}) {
  const { t } = useTranslation("create");
  return (
    <Card className="p-5">
      <details className="group">
        <summary
          className={cx(
            "flex cursor-pointer items-center gap-1.5 text-sm text-fg",
            "marker:content-['']",
          )}
        >
          <Icon
            name="chevron"
            className="size-3.5 transition-transform duration-200 ease-smooth group-open:rotate-90"
          />
          {t("regions.label")}
          <span className="text-fg-subtle" data-testid="region-summary">
            — {summarize(catalogue, value, t)}
          </span>
        </summary>
        <div className="mt-5">
          <RegionPicker
            catalogue={catalogue}
            loading={loading}
            value={value}
            onChange={onChange}
          />
        </div>
      </details>
    </Card>
  );
}

/** What the folded section says: the region the workload will land in, as far as is known. */
export function summarize(
  catalogue: RegionCatalogue | undefined,
  value: string | null,
  t: TFunction<"create">,
): string {
  if (!catalogue) return t("regions.auto");
  const label = (code: string | null) =>
    catalogue.data.find((region) => region.code === code)?.label ?? code ?? "";
  if (catalogue.placement.pinned) return label(catalogue.placement.pinned);
  if (value) return label(value);
  const auto = catalogue.placement.suggested;
  return auto ? t("regions.autoWith", { region: label(auto) }) : t("regions.auto");
}
